import React from "react";
import CollapsibleCard from "../../../ForBrands/ui/CollapsibleCard";
import InsightList from "../../../ForBrands/ui/InsightList";
import { ArchetypeDot } from "../../../ForBrands/ui/LegendItem";
import { cx } from "./perfPieces";

// Historical Comps (Jayson's A6, spec B3 f and B10): the three closest past
// players of the same division (named, owner approved; how similar, their
// program, season and archetype, their line, and where they went next),
// then how often players in each Impact rating band reached D1 or pro, the
// athlete's own band in bm-orange, and the Comps read. The page hides the
// card when the payload has no comparison pool. `view` is
// athletePerformanceModel.compsView().

const HistoricalCompsCard = ({ view, cardProps = {} }) => (
  <CollapsibleCard title="Historical Comps" subtitle={view.lede} {...cardProps}>
    {view.comps.length > 0 ? (
      // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
      <ul role="list" aria-label="Closest past players" className="flex flex-col gap-2">
        {view.comps.map((c) => (
          <li key={c.key} className="flex min-w-0 flex-col gap-1 rounded-[8px] border border-bm-border bg-white px-4 py-3">
            <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="min-w-0 break-words bm-label-l text-bm-ink">{c.name}</span>
              {c.similarity && <span className="shrink-0 bm-label-m text-bm-orange">{c.similarity}</span>}
            </span>
            <span className="flex flex-wrap items-center gap-x-[6px] gap-y-1 bm-body-s text-bm-muted">
              {c.meta && <span className="break-words">{c.meta}</span>}
              {c.archetype && (
                <span className="inline-flex items-center gap-[6px]">
                  {c.meta && <span aria-hidden="true">·</span>}
                  <ArchetypeDot color={c.color} size={8} />
                  {c.archetype}
                </span>
              )}
            </span>
            {c.statLine && <span className="bm-body-s tabular-nums text-bm-ink">{c.statLine}</span>}
            <span className={cx("break-words bm-body-s", c.outcome ? "text-bm-ink" : "text-bm-muted")}>{c.outcome || "No later college or pro record"}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="bm-body-s text-bm-muted">No close past profiles in this pool.</p>
    )}
    {view.bands.length > 0 && (
      <div className="flex flex-col gap-2">
        <h3 className="m-0 bm-h-xs text-bm-ink">{view.bandHeading}</h3>
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
        <ul role="list" aria-label={view.bandHeading} className="flex flex-col gap-2">
          {view.bands.map((b) => (
            <li key={b.key} className="grid grid-cols-[minmax(0,112px)_minmax(0,1fr)_auto] items-center gap-3">
              <span className={cx("min-w-0 break-words bm-label-m", b.own ? "text-bm-orange" : "text-bm-ink")}>{b.label}</span>
              <span role="img" aria-label={b.ariaLabel} className="relative block h-[10px] overflow-hidden rounded-full bg-bm-mutedbg">
                <span aria-hidden="true" className={cx("absolute inset-y-0 left-0 rounded-full", b.own ? "bg-bm-orange" : "bg-bm-tertiary")} style={{ width: `${b.width}%` }} />
              </span>
              <span aria-hidden="true" className="whitespace-nowrap bm-body-s text-bm-muted">
                <strong className={cx("font-semibold", b.own ? "text-bm-orange" : "text-bm-ink")}>{b.rate}</strong>
                {` of ${b.n}`}
              </span>
            </li>
          ))}
        </ul>
      </div>
    )}
    <InsightList insights={view.insights} variant="read" label="Comps read" />
  </CollapsibleCard>
);

export default HistoricalCompsCard;
